import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Databoard, DataboardBody } from '../../../../../../web/components/Databoard';
import { DataGrid, DataGridColumn, DataGridColumns, DataGridRows } from '../../../../../../web/components/DataGrid';
import { EntityTablePlaceholder } from '../../../../../../web/components/entities/EntityTablePlaceholder';
import { Popover, PopoverContent, PopoverTrigger } from '../../../../../../web/components/Popover';
import { DisclosureIcon } from '../../../../../../web/components/svgs/icons/DisclosureIcon';
import { Waypoint } from 'react-waypoint';
import { toSentenceCase } from '../../../../../../web/utils/toSentenceCase';
import { CallAnalysisData } from '../../types';
import { CallAnalysisRow } from '../CallAnalysisRow';
import { styleSheet } from './styles';

interface CallAnalysisTableProps {
	calls: CallAnalysisData[];
	isLoading: boolean;
	sort: Api.ISortDescriptor;
	onSort: (sort: Api.ISortDescriptor) => void;
	onClearFilters: () => void;
	loadMore: (shouldNotTriggerLoading?: boolean) => void;
	hasNextPage?: boolean;
	isFetchingNextPage?: boolean;
	// Multi-selection props
	selectedCallIds?: string[];
	onSelectCall?: (callId: string, selected: boolean, isBulk?: boolean) => void;
	onSelectAll?: (selected: boolean) => void;
	onAnalyzeMultipleCalls?: (analysisType: string) => void;
	isAllSelected?: boolean;
	isPartialSelected?: boolean;
	isCallSelected?: (callId: string) => boolean;
	// SDR click handler
	onSDRClick?: (userId: string, userName: string) => void;
}

export const CallAnalysisTable: React.FC<CallAnalysisTableProps> = observer(
	({
		calls,
		isLoading,
		sort,
		onSort,
		onClearFilters,
		loadMore,
		hasNextPage,
		isFetchingNextPage,
		selectedCallIds: _selectedCallIds = [],
		onSelectCall,
		onSelectAll,
		onAnalyzeMultipleCalls: _onAnalyzeMultipleCalls,
		isAllSelected = false,
		isPartialSelected = false,
		isCallSelected,
		onSDRClick,
	}) => {
		const handleSort = (newSort: Api.ISortDescriptor) => () => {
			onSort(newSort);
		};

		const sortDirectionDesc = sort.sort === 'desc';

		const getCallTimeHeader = () => {
			const sortByStringValue = sort.sortBy;
			return sortByStringValue === 'creationDate' ? `Call Time (${toSentenceCase(sort.sort)}.)` : 'Call Time';
		};

		const getDurationHeader = () => {
			const sortByStringValue = sort.sortBy;
			return sortByStringValue === 'duration' ? `Duration (${toSentenceCase(sort.sort)}.)` : 'Duration';
		};

		// Should display waypoint for load more functionality
		const shouldDisplayWaypoint = hasNextPage && !isFetchingNextPage && calls.length > 0;

		// Multi-selection logic
		const hasMultiSelectFeature = !!(onSelectCall && onSelectAll);
		const allCallsWithTranscription = calls.filter(call => call.hasTranscription);

		const handleSelectAll = () => {
			if (onSelectAll) {
				onSelectAll(!isAllSelected);
			}
		};

		return (
			<Databoard styles={[styleSheet.databoard]}>
				<DataboardBody styles={[styleSheet.databoardBody]} data-scroll-restoration-id='call-analysis-table'>
					<DataGrid styles={[styleSheet.dataGrid]}>
						<DataGridColumns>
							{hasMultiSelectFeature && (
								<DataGridColumn styles={[styleSheet.columnHeader]} sticky>
									<input
										type='checkbox'
										checked={isAllSelected}
										ref={input => {
											if (input) input.indeterminate = isPartialSelected && !isAllSelected;
										}}
										onChange={() => handleSelectAll()}
										disabled={allCallsWithTranscription.length === 0}
									/>
								</DataGridColumn>
							)}
							<DataGridColumn styles={[styleSheet.columnHeader]} sticky>
								Contact / Company
							</DataGridColumn>
							<DataGridColumn styles={[styleSheet.columnHeader]} sticky>
								SDR
							</DataGridColumn>
							<DataGridColumn styles={[styleSheet.columnHeader]} sticky>
								Call Outcome
							</DataGridColumn>
							<DataGridColumn styles={[styleSheet.columnHeader]} sticky>
								<Popover>
									<PopoverTrigger asChild>
										<div className={css(styleSheet.sortTrigger)}>
											<span>{getCallTimeHeader()}</span>
											<span className={css(styleSheet.sortIcon, sortDirectionDesc && styleSheet.sortIconFlipped)}>
												<DisclosureIcon type='chevron' />
											</span>
										</div>
									</PopoverTrigger>
									<PopoverContent align='start' styles={[styleSheet.sortMenu]} sideOffset={4}>
										<div
											className={css(styleSheet.sortMenuItem)}
											onClick={handleSort({
												sort: 'desc',
												sortBy: 'creationDate',
											})}
										>
											Most Recent
										</div>
										<div
											className={css(styleSheet.sortMenuItem)}
											onClick={handleSort({
												sort: 'asc',
												sortBy: 'creationDate',
											})}
										>
											Oldest First
										</div>
									</PopoverContent>
								</Popover>
							</DataGridColumn>
							<DataGridColumn styles={[styleSheet.columnHeader]} sticky>
								<Popover>
									<PopoverTrigger asChild>
										<div className={css(styleSheet.sortTrigger)}>
											<span>{getDurationHeader()}</span>
											<span className={css(styleSheet.sortIcon, sortDirectionDesc && styleSheet.sortIconFlipped)}>
												<DisclosureIcon type='chevron' />
											</span>
										</div>
									</PopoverTrigger>
									<PopoverContent align='start' styles={[styleSheet.sortMenu]} sideOffset={4}>
										<div
											className={css(styleSheet.sortMenuItem)}
											onClick={handleSort({ sort: 'desc', sortBy: 'duration' })}
										>
											Longest First
										</div>
										<div
											className={css(styleSheet.sortMenuItem)}
											onClick={handleSort({ sort: 'asc', sortBy: 'duration' })}
										>
											Shortest First
										</div>
									</PopoverContent>
								</Popover>
							</DataGridColumn>
							<DataGridColumn styles={[styleSheet.columnHeader]} sticky>
								Transcription Status
							</DataGridColumn>
							<DataGridColumn styles={[styleSheet.columnHeader]} sticky>
								Transcription Copy
							</DataGridColumn>
						</DataGridColumns>
						{calls.length ? (
							<DataGridRows>
								{calls.map((call: CallAnalysisData) => {
									const isSelected = isCallSelected ? isCallSelected(call.phoneCall.id) : false;
									return (
										<CallAnalysisRow
											key={`call-analysis-row-${call.phoneCall.id}`}
											call={call}
											hasMultiSelectFeature={hasMultiSelectFeature}
											isSelected={isSelected}
											onSelect={onSelectCall}
											onSDRClick={onSDRClick}
										/>
									);
								})}
							</DataGridRows>
						) : null}
					</DataGrid>

					{!calls.length && !isLoading ? (
						<EntityTablePlaceholder
							className={css(styleSheet.placeholder, styleSheet.emptyStateContainer)}
							onClearButtonClicked={onClearFilters}
							type='contact'
							message='No calls found matching your criteria.'
						/>
					) : null}

					{/* Load more functionality with Waypoint */}
					{shouldDisplayWaypoint && (
						<Waypoint key='load-more-trigger' bottomOffset='-200px' onEnter={() => loadMore()} />
					)}
				</DataboardBody>
			</Databoard>
		);
	}
);
